import * as React from "react";
import {
  DogApi,
  EDogType,
  ERequestState,
  ExpatriationRequestApi,
  ExpatriationRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
} from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import * as Yup from "yup";
import { ETLCodes } from "../../../../locales";
import {
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useHistory } from "react-router-dom";
import { FGCustomInput, FGListen, FieldGroup } from "nsitools-react";
import { FormikProps } from "formik";
import { Radio, RadioGroup } from "@blueprintjs/core";
export interface IExpatriationRequestDetailPanelProps {
  expatriationRequestId: number;
  editMode: boolean;
}

export const ExpatriationRequestDetailPanel: React.FunctionComponent<IExpatriationRequestDetailPanelProps> = ({
  expatriationRequestId,
  editMode,
}) => {
  const api = useApiService(ExpatriationRequestApi);
  const personApi = useApiService(PersonApi);
  const history = useHistory();
  const dogApi = useApiService(DogApi);
  const { openNewTab } = useWindow();
  const { t } = useTl();
  const { data, loading, saving, saveItem, refresh } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: (id = expatriationRequestId) =>
          id > 0
            ? api.getExpatriationRequest(id)
            : new ExpatriationRequestDto({
                request: RequestDto.fromJS({
                  requestDate: new Date(),
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        saveApiFn: (d: ExpatriationRequestDto) => api.save(d),
        onSavedRoute: (d: ExpatriationRequestDto) =>
          `${ERoutes.expatriationRequest}/${d.expatriationRequestId}/detail/edit`,
      }),
      [api, expatriationRequestId]
    )
  );
  const [dogId, setdogId] = React.useState<number>(null);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number().nullable().required(t(ETLCodes.Required)),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (expatriationRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isDeceased(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (expatriationRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.hasCountry(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            if (!dogId) {
              return true;
            }
            const res = await dogApi.isDogTemporaryOwner(dogId, value);
            return !res.value;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data?.request?.status, dogApi, dogId, expatriationRequestId, personApi, t]);

  const statusEditable = React.useMemo(() => {
    if (data) {
      if (data.request.status) {
        return data.request.status === ERequestState.REQUESTED;
      } else {
        return true;
      }
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  return (
    <SmallFormGenerator
      initialValues={data}
      editMode={editMode}
      onSubmit={saveItem}
      validationSchema={RequestedFormSchema}
      loading={loading}
      onCancel={() => history.push(ERoutes.expatriationRequest)}
      saving={saving}
      hideButtons={data?.request?.status === ERequestState.EXECUTED}
    >
      <FGListen field="dogId" onChanged={setdogId} />
      <FieldGroup
        columns={2}
        fieldsetProps={{
          title: t(ETLCodes.RequestData),
          rightElement: expatriationRequestId > 0 && (
            <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
          ),
        }}
      >
        <div>
          <FGSRSHDateMaskInput
            name="request.requestDate"
            label={t(ETLCodes.RequestDate)}
            requiredMark
            readonly={!statusEditable || data?.request?.isBlocked}
          />
          {data?.request?.status === ERequestState.EXECUTED && (
            <FieldGroup columns={2}>
              <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
            </FieldGroup>
          )}
          <FGDogSearchInput
            name="dogId"
            label={t(ETLCodes.Dog)}
            defaultSearch={{ types: [EDogType.BelgianDogWithPedigree, EDogType.ImportDog] }}
            onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            readonly={!statusEditable || data?.request?.isBlocked}
            info={
              expatriationRequestId > 0 && (
                <SharepointButton existsInShp={dogExistsInShp} loading={dogShpLoading} onClick={openOrCreateDogInShp} />
              )
            }
          />

          <FGPeopleSearchInput
            name="request.personId"
            label={t(ETLCodes.Contact)}
            onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
            readonly={!statusEditable || data?.request?.isBlocked}
            requiredMark
            dogId={dogId}
            showWarning={true}
          />
          <FGCustomInput label={t(ETLCodes.DogType)} name="isForeignDog">
            {({ formik }) => (
              <RadioGroup
                inline={true}
                selectedValue={"" + formik?.values.isForeignDog}
                onChange={(c) => {
                  formik.setFieldValue("isForeignDog", c.currentTarget.value);
                }}
              >
                <Radio key={1} label={t(ETLCodes.BelgianDog)} value={"false"} checked={!formik?.values.isForeignDog} />
                <Radio key={1} label={t(ETLCodes.ForeignDog)} value={"true"} checked={formik?.values.isForeignDog} />
              </RadioGroup>
            )}
          </FGCustomInput>
        </div>
      </FieldGroup>
    </SmallFormGenerator>
  );
};
